import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/Sections";
import { Field, SelectField, TextArea } from "@/components/site/FormControls";
import { submitWholesale } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const ROLES = ["Wholesaler", "Investor", "Acquisition Company", "Agent / Broker", "Deal Sourcer", "Other"];
const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily", "Condo", "Townhouse", "Mixed Use", "Commercial", "Land", "Other"];
const REHAB = ["Move-In Ready", "Cosmetic Rehab", "Medium Rehab", "Heavy Rehab", "Full Gut Renovation", "Unknown"];
const YN = ["Yes", "No"];
const YNU = ["Yes", "No", "Unknown"];

const initial = {
  first_name: "", last_name: "", phone: "", email: "", role: "",
  property_address: "", city: "", county: "", state: "", property_type: "",
  asking_price: "", estimated_arv: "", estimated_rehab: "", rehab_level: "",
  under_contract: "No", assignable: "Unknown", notes: "",
};

export default function RegisterDeal() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.email || !form.property_address) {
      toast.error("Please complete name, contact and property address.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitWholesale({ ...form, source: "quick-deal" });
      setDone(res);
      toast.success("Deal registered for review.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Registered" title="Deal Registered for Review." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            {typeof done.deal_score === "number" && (
              <div className="mx-auto mt-6 inline-flex flex-col items-center rounded-2xl border border-gold/40 bg-[#FAF8F1] px-8 py-5">
                <span className="font-mono-x text-xs uppercase tracking-widest text-gold">Vertexhub Deal Score</span>
                <span className="font-display text-5xl font-bold text-[#0B132B]">{done.deal_score}</span>
                <span className="text-sm text-slate-500">{done.score_band}</span>
              </div>
            )}
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Your deal has been submitted for review. Submission does not guarantee acceptance, buyer placement, financing, sale or closing.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="Register Your Deal"
        title="Register Your Deal."
        subtitle="A few basic questions so we can analyze the numbers and match it with qualified buyers. Want the full version? Use the Wholesale page for the complete submission."
      />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <form data-testid="register-deal-form" onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <h3 className="font-mono-x text-xs uppercase tracking-widest text-gold">Your Details</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" name="first_name" value={form.first_name} onChange={set} required half testid="rd-first-name" />
              <Field label="Last Name" name="last_name" value={form.last_name} onChange={set} required half testid="rd-last-name" />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} required half testid="rd-phone" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={set} required half testid="rd-email" />
              <SelectField label="Role" name="role" value={form.role} onChange={set} options={ROLES} half testid="rd-role" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">The Property</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Property Address" name="property_address" value={form.property_address} onChange={set} required testid="rd-address" />
              <Field label="City" name="city" value={form.city} onChange={set} half testid="rd-city" />
              <Field label="County" name="county" value={form.county} onChange={set} half testid="rd-county" />
              <Field label="State" name="state" value={form.state} onChange={set} half testid="rd-state" />
              <SelectField label="Property Type" name="property_type" value={form.property_type} onChange={set} options={PROP_TYPES} half testid="rd-prop-type" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">The Numbers</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Asking Price" name="asking_price" value={form.asking_price} onChange={set} half testid="rd-asking" />
              <Field label="Estimated ARV" name="estimated_arv" value={form.estimated_arv} onChange={set} half testid="rd-arv" />
              <Field label="Estimated Rehab" name="estimated_rehab" value={form.estimated_rehab} onChange={set} half testid="rd-rehab" />
              <SelectField label="Rehab Level" name="rehab_level" value={form.rehab_level} onChange={set} options={REHAB} half testid="rd-rehab-level" />
              <SelectField label="Under Contract?" name="under_contract" value={form.under_contract} onChange={set} options={YN} half testid="rd-under-contract" />
              <SelectField label="Assignable?" name="assignable" value={form.assignable} onChange={set} options={YNU} half testid="rd-assignable" />
              <TextArea label="Notes" name="notes" value={form.notes} onChange={set} placeholder="Anything else about this deal…" testid="rd-notes" />
            </div>

            <button type="submit" disabled={loading} data-testid="register-deal-submit-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{loading ? "Registering…" : "Register My Deal"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
